// Follow-up prompt sets indexed by detected intent. Used to render 2–3
// chips below the most recent assistant message so the conversation
// doesn't end at one turn. The chips use {baby} as a placeholder which
// gets substituted at render time with the active baby's name.
//
// Why hardcoded vs LLM-generated: speed (no extra latency), cost
// (zero tokens), reliability (no failure mode), and these prompts can
// be quality-controlled in a way generated ones can't. Worth replacing
// with a per-message generated set later if data shows it's worth the
// latency hit.

import { isGerman } from '@/i18n/locale';

export type Intent =
  | 'feeding'
  | 'sleep'
  | 'health'
  | 'development'
  | 'mood'
  | 'general';

interface FollowUp {
  emoji: string;
  text: string; // may contain {baby} placeholder
}

const FOLLOWUPS: Record<Intent, FollowUp[]> = {
  feeding: [
    { emoji: '🤔', text: 'How can I tell if {baby} is getting enough?' },
    { emoji: '⏰', text: 'How often should we be feeding right now?' },
    { emoji: '📈', text: 'What about growth — what should I be tracking?' },
  ],
  sleep: [
    { emoji: '🌙', text: 'What does a healthy sleep schedule look like at this age?' },
    { emoji: '😴', text: 'What if {baby} keeps waking up at night?' },
    { emoji: '🧘', text: 'Is sleep training right for us?' },
  ],
  health: [
    { emoji: '🌡️', text: 'When should I actually call the pediatrician?' },
    { emoji: '👀', text: 'What warning signs should I watch for?' },
    { emoji: '💊', text: 'Is there anything safe I can do at home?' },
  ],
  development: [
    { emoji: '🌱', text: "What's coming next developmentally?" },
    { emoji: '🤲', text: 'Anything I can do to help {baby} along?' },
    { emoji: '⚠️', text: 'When should I be concerned about a delay?' },
  ],
  mood: [
    { emoji: '🤍', text: "Is what I'm feeling normal?" },
    { emoji: '🆘', text: 'Where can I get real support if I need it?' },
    { emoji: '☕', text: 'What can I do for myself today?' },
  ],
  general: [
    { emoji: '💭', text: 'Tell me more about that' },
    { emoji: '✅', text: 'Is what I’m doing typical for our age?' },
    { emoji: '➕', text: 'What else should I know?' },
  ],
};

// German parallel — same intent keys, same {baby} placeholder support.
// Tone: warm, "du" form, conversational. Used when isGerman() is true.
const FOLLOWUPS_DE: Record<Intent, FollowUp[]> = {
  feeding: [
    { emoji: '🤔', text: 'Wie merke ich, ob {baby} genug bekommt?' },
    { emoji: '⏰', text: 'Wie oft sollten wir gerade stillen oder füttern?' },
    { emoji: '📈', text: 'Worauf sollte ich beim Wachstum achten?' },
  ],
  sleep: [
    { emoji: '🌙', text: 'Wie sieht ein gesunder Schlafrhythmus in diesem Alter aus?' },
    { emoji: '😴', text: 'Was, wenn {baby} nachts immer wieder aufwacht?' },
    { emoji: '🧘', text: 'Wäre Schlaftraining etwas für uns?' },
  ],
  health: [
    { emoji: '🌡️', text: 'Wann sollte ich wirklich die Kinderärztin anrufen?' },
    { emoji: '👀', text: 'Auf welche Warnzeichen sollte ich achten?' },
    { emoji: '💊', text: 'Gibt es etwas, das ich zu Hause sicher tun kann?' },
  ],
  development: [
    { emoji: '🌱', text: 'Was kommt entwicklungsmäßig als Nächstes?' },
    { emoji: '🤲', text: 'Kann ich {baby} dabei irgendwie unterstützen?' },
    { emoji: '⚠️', text: 'Wann sollte ich mir Sorgen um eine Verzögerung machen?' },
  ],
  mood: [
    { emoji: '🤍', text: 'Ist das normal, was ich gerade fühle?' },
    { emoji: '🆘', text: 'Wo finde ich richtige Unterstützung, wenn ich sie brauche?' },
    { emoji: '☕', text: 'Was kann ich heute für mich selbst tun?' },
  ],
  general: [
    { emoji: '💭', text: 'Erzähl mir mehr darüber' },
    { emoji: '✅', text: 'Ist das, was ich tue, typisch für unser Alter?' },
    { emoji: '➕', text: 'Was sollte ich sonst noch wissen?' },
  ],
};

// Resolve {baby} placeholders. Defensive: if babyName is empty, fall
// back to a neutral pronoun so we don't render "How can I tell if  is…"
function fill(text: string, babyName: string): string {
  const safe = babyName.trim() || 'baby';
  return text.replace(/\{baby\}/g, safe);
}

// Returns up to 3 follow-up prompts for the given intent. Pass null/undefined
// to get the 'general' bucket. Caller controls how many to render — we
// always return all 3 so caller can slice or shuffle.
export function getFollowUps(
  intent: Intent | string | null | undefined,
  babyName: string
): Array<{ emoji: string; text: string }> {
  const key = isIntent(intent) ? intent : 'general';
  const map = isGerman() ? FOLLOWUPS_DE : FOLLOWUPS;
  return map[key].map((f) => ({ emoji: f.emoji, text: fill(f.text, babyName) }));
}

function isIntent(v: unknown): v is Intent {
  return (
    v === 'feeding' ||
    v === 'sleep' ||
    v === 'health' ||
    v === 'development' ||
    v === 'mood' ||
    v === 'general'
  );
}
